import axios from 'axios';
import {
  MoSearchResponse,
  MoTableData,
  MyOrderResponse,
  SearchRequest,
  CancelOrderResponse,
  MyOrder,
  FullSegment,
  TicketMo,
  OpenLogResponse,
  Order,
  MoSegmentEditorOtherResponse,
} from 'ts/shared/interfaces/MOResponse';
import { getStatus } from 'constants/mo';
import { formatMyOrder, markOrdersWithSameId } from 'utils/shared/my-order';
import { GroupRequestModel } from 'ts/shared/classes/GroupRequestModel';
import { Select } from 'ts/interfaces/Generic';

export interface PnrToDocketParams {
  orderId: number;
  segmentId: number;
  productType?: string;
  ticketId?: number;
}

export interface GetTicketParams {
  orderId: number;
  segmentId: number;
  ticketId: number;
}

export interface SaveDocketParams {
  orderId: number;
  segmentId: number;
  backOfficeId: string;
  useTopBackOfficeId: boolean;
}

export interface ChangeRequestParams extends PnrToDocketParams {
  requestMessage: string;
  orderId: number;
  subject?: string;
  commentPublic?: boolean;
  ticketId?: number;
}

export interface PrintParams {
  orderId: number;
  segmentId: number;
  orderReportType: string;
}

export interface CancelOrderParams {
  id: number;
  segid: number;
  recaptchaToken: string;
  depId: number;
  branchid: number;
  usercode: number;
}

interface OpenLogParams {
  orderId: number;
  segmentId: number;
}

export const openLog = async (params: OpenLogParams): Promise<OpenLogResponse | undefined> => {
  try {
    const url = 'MyOrders/OpenLog';
    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const formatSearchData = (data?: SearchRequest) => {
  if (!data) return undefined;
  const departmentsOptions = data.departments
    .map((d) => ({
      label: d.departmentDesc + ' | ' + d.departmentId,
      value: d.departmentId + '',
      html: (
        <div className="flex-between">
          <span>{d.departmentDesc}</span>
          <span>{d.departmentId}</span>
        </div>
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  departmentsOptions.unshift({
    label: 'All departments',
    value: '-1',
    html: <div>All departments</div>,
  });

  let supplierOptions = data.suppliers
    .map((s) => ({
      label: s.suppDesc + (s.suppCode ? ' | ' + s.suppCode : ''),
      value: s.suppCode,
      html: (
        <div className="flex-between">
          <span>{s.suppDesc}</span>
          <span>{s.suppCode}</span>
        </div>
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  supplierOptions.unshift({ label: '[all]', value: '', html: <div>[All]</div> });

  const branchesOptions = data.branches
    .map((b) => ({
      label: b.branchDesc + ' | ' + b.branchId,
      value: b.branchId + '',
      data: {
        departmentId: b.departmentId,
        visible: true,
      },
      html: (
        <div className="flex-between">
          <span>{b.branchDesc}</span>
          <span>{b.branchId}</span>
        </div>
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  branchesOptions.unshift({
    label: 'All branches',
    value: '-1',
    data: { departmentId: -1, visible: true },
    html: <div>All branches</div>,
  });

  const usersOptions = data.users
    .map((u) => {
      const { branchId } = data;
      const visible = branchId === -1 || branchId === u.branchId;
      return {
        label: u.fullName + ' | ' + u.user_code,
        value: u.user_code + '',
        data: {
          departmentId: u.departmentId,
          branchId: u.branchId,
          visible: visible,
        },
        html: (
          <div className="flex-between">
            <span>{u.fullName}</span>
            <span>{u.user_code}</span>
          </div>
        ),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
  usersOptions.unshift({
    label: 'All users',
    value: '-1',
    data: { departmentId: -1, branchId: -1, visible: true },
    html: <div>All users</div>,
  });

  const statusesOptions = data.orderStatusList.map((s: any) => {
    const statuses: string[] = s.text.replaceAll(' ', '').split('&');
    return {
      label: s.text,
      value: s.value,
      html: (
        <div>
          {statuses.map((st) => (
            <span key={st} className={'mo-status mr-5 ' + getStatus(st)}>
              {st}
            </span>
          ))}
        </div>
      ),
    };
  });
  statusesOptions.shift();
  statusesOptions.unshift({ label: '[ALL]', value: 'ALL', html: <div>[ALL]</div> });

  const typesOptions = data.orderProductTypeList.map((s) => ({
    label: s.text,
    value: s.value,
  }));

  return {
    ...data,
    departmentsOptions,
    branchesOptions,
    usersOptions,
    statusesOptions,
    typesOptions,
    supplierOptions,
  };
};

export const getSearchData = async (): Promise<MoSearchResponse | undefined> => {
  try {
    const url = 'MyOrders/GetSearchFilters';
    const { data } = await axios.get<MoSearchResponse>(url);

    return {
      ...data,
      searchRequest: {
        ...data.searchRequest,
        orderSupplier: '',
        orderItem: '',
        orderStatus: 'RQ;OK;HK;HX;VOUCHER',
      },
    };
  } catch (err) {
    return undefined;
  }
};

export const search = async (
  body: SearchRequest,
  recaptchaToken: string,
  abortSignal: AbortSignal
): Promise<{ table: MoTableData[]; data: MyOrder[] } | undefined> => {
  try {
    const {
      users,
      branches,
      departments,
      suppliers,
      departmentComminssion,
      branchComminssion,
      orderStatusList,
      orderProductTypeList,
      ...params
    } = body;
    const url = 'MyOrders/SearchOrders';
    const { data } = await axios.post(url, { ...params, recaptchaToken }, { signal: abortSignal });
    const markedResult = markOrdersWithSameId(data.myOrders);
    const result = markedResult.map((item: MyOrder) => formatMyOrder(item));

    return { table: result, data: markedResult };
  } catch (err) {
    return undefined;
  }
};

export const getDetails = async (id: string): Promise<MyOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/Details';
    const { data } = await axios.get<MyOrderResponse>(url, { params: { id } });
    const segments = data.myOrder.order.segments.map((segment) => {
      const supplier = data.myOrder.suppliersByCode[segment.sysSuppCode];
      return { ...segment, supplier };
    });
    return { ...data, myOrder: { ...data.myOrder, order: { ...data.myOrder.order, segments } } };
  } catch (err) {
    return undefined;
  }
};

export const getVoucher = async (
  segment: FullSegment,
  boVoucher: string,
  TeamVoucher?: boolean,
  roomids?: string
) => {
  try {
    const url = 'MyOrders/PrintVoucherByteArray';
    const config = {
      responseType: 'blob' as 'json',
      params: {
        id: segment.orderId,
        segid: segment.orderSegId,
        type: segment.productType,
        boVoucher,
        SysSuppCode: segment.sysSuppCode,
        TeamVoucher,
        roomids,
      },
    };
    const { data } = await axios.get(url, config);
    var blob = new Blob([data]);
    var link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `voucher_${segment.orderId}.${segment.orderSegId}.pdf`;
    link.click();
    return data;
  } catch (err) {
    return undefined;
  }
};

export const checkStatus = async (
  segment: FullSegment
): Promise<CancelOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/CheckStatus';
    const config = { params: { id: segment.orderId, segid: segment.orderSegId } };
    const { data } = await axios.get<MyOrderResponse>(url, config);
    return data;
  } catch (err) {
    return undefined;
  }
};

export const cancelOrder = async (
  params: CancelOrderParams
): Promise<CancelOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/CancelOrder';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const copyOrderJson = async (params: OpenLogParams): Promise<string | undefined> => {
  try {
    const url = 'MyOrders/CopyOrderJson';
    const data = (await axios.post(url, params)).data;
    if (typeof data === 'string') {
      return data;
    }
    if (data?.clipboardOrder) {
      return data.clipboardOrder;
    }
    if (data != null) {
      return JSON.stringify(data);
    }
    return undefined;
  } catch (err) {
    return undefined;
  }
};

export const pnrToDocket = async (
  params: PnrToDocketParams
): Promise<CancelOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/PnrToDocket';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const changeRequest = async (
  params: ChangeRequestParams
): Promise<CancelOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/ChangeRequest';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const createTicket = async (
  params: ChangeRequestParams
): Promise<CancelOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/CreateHelpDeskTicket';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const updateTicket = async (params: ChangeRequestParams): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/UpdateHelpDeskTicket';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const getTicket = async (params: GetTicketParams): Promise<TicketMo | undefined> => {
  try {
    const url = 'MyOrders/GetHelpDeskTicket';
    return (await axios.post(url, params)).data.tickets.ticketResults[0].results[0];
  } catch (err) {
    return undefined;
  }
};

export const print = async (params: PrintParams): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/PrintOrderInformation';
    return (await axios.post<MyOrderResponse>(url, params, { responseType: 'arraybuffer' })).data;
  } catch (err) {
    return undefined;
  }
};

export const previewDoc = async (file: string): Promise<string | undefined> => {
  try {
    const url = 'MyOrders/ViewFile';
    const { data } = await axios.get(url, { params: { file } });
    return data.fileBase64;
  } catch (err) {
    return undefined;
  }
};

interface OrderToEmailParams {
  orderId: number;
  segmentId: number;
  email?: string;
  sendEmailToMe: boolean;
  reportType: string;
  roomids?: string;
  teamVoucher?: boolean;
}

export const orderToEmail = async (data: OrderToEmailParams) => {
  try {
    const url = 'MyOrders/OrderReportToEmail';
    return (await axios.post(url, data)).data;
  } catch (err) {
    return undefined;
  }
};

interface AddRemarkParams {
  recaptchaToken: string;
  remarkCode: string;
  segmentId: number;
  orderId: number;
  notes: string;
}

export const addRemark = async (
  params: AddRemarkParams
): Promise<CancelOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/AddRemark';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const generateReport = async (
  body: SearchRequest,
  params: {
    recaptchaToken: string;
    reportType: string;
    segments: string;
    reportView: string;
    reportPrice: string;
  }
): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/GenerateReport';
    const response = await axios.post(
      url,
      {
        ...body,
        ...params,
      },
    );

    return response.data;
  } catch (err) {
    return undefined;
  }
};

export const saveDocket = async (
  params: SaveDocketParams
): Promise<MyOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/SaveDocketNumber';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const sendGroupRequest = async (body: GroupRequestModel) => {
  try {
    const url = 'Account/GroupRequest';
    return (
      await axios.post(url, {
        ...body,
        hotelRequest: {
          ...body.hotelRequest,
          numberOfRooms: parseInt(body.hotelRequest.numberOfRooms),
        },
        flightRequest: {
          ...body.flightRequest,
          numberOfPassengers: parseInt(body.flightRequest.numberOfPassengers),
        },
      })
    ).data;
  } catch (err) {
    return undefined;
  }
};

export interface LockPnrParams {
  orderId: number;
  segmentId: number;
  lockPnr: boolean;
}

export const lockPnr = async (params: LockPnrParams): Promise<CancelOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/LockPnr';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};
interface ChangeUserParams {
  orderId: number;
  segmentId: number;
  targetUserCode: number;
}
export const changeUser = async (
  params: ChangeUserParams
): Promise<CancelOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/ChangeOrderUser';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

interface UploadDocumentParams {
  files: File[];
  orderId: number;
  segmentId: number;
}

export const uploadDocument = async (params: UploadDocumentParams): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/UploadFiles';
    const formData = new FormData();
    const headers = {
      'Content-Type': 'multipart/form-data',
    };

    params.files.forEach((file) => {
      formData.append('Files', file);
    });

    formData.append('OrderId', params.orderId.toString());
    formData.append('SegId', params.segmentId.toString());

    const fileRes = (await axios.post(url, formData, { headers })).data;

    return fileRes.uploadedFiles;
  } catch (err) {
    return undefined;
  }
};

interface DeleteDocumentParams {
  orderId: number;
  segId: number;
  documentId: number;
}
export const deleteDocument = async (params: DeleteDocumentParams): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/DeleteFile';

    const response = (await axios.post(url, params)).data;

    return response;
  } catch (err) {
    return undefined;
  }
};
export const downloadDocument = async (
  params: DeleteDocumentParams,
  fileName: string
): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/DownloadFile';

    const config = {
      responseType: 'blob' as 'json',
      params: {
        orderId: params.orderId,
        segId: params.segId,
        docId: params.documentId,
      },
    };

    const axiosResponse = await axios.get(url, config);
    const response = axiosResponse.data;
    const contentType = axiosResponse.headers['content-type'] || 'application/octet-stream';

    const blob = new Blob([response], { type: contentType });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);

    return response;
  } catch (err) {
    return undefined;
  }
};

interface SendReportPdfByEmailParams {
  email: string;
  url: string;
}
export const sendReportPdfByEmail = async (
  params: SendReportPdfByEmailParams
): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/SendReportPdfByEmail';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};
interface LoginAsParams {
  userCode: number;
  departmentId: number;
  branchId: number;
  recaptchaToken: string;
}

export const loginAs = async (params: LoginAsParams): Promise<any | undefined> => {
  try {
    const url = 'Account/LoginAs';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const logoutAs = async (recaptchaToken: string): Promise<any | undefined> => {
  try {
    const url = 'Account/LogoutAs';
    return (await axios.post<MyOrderResponse>(url, { recaptchaToken })).data;
  } catch (err) {
    return undefined;
  }
};

interface CreateCustomPaymentBlockParams {
  orderId: number;
  segmentId: number;
}
export interface CreateCustomPaymentBlockResponse {
  credit2000PaymentInfo: any;
  isRevolut: boolean;
  revolutPaymentsLevel1: Revolut;
  revolutPaymentsLevel2: Revolut;
  hasErrors: boolean;
  couponCurrency: string;
  coupon: number;
}
interface Revolut {
  currency: string;
  price: number;
  secondPrice: number;
  secondCurrency: string;
}
export const createCustomPaymentBlock = async (params: CreateCustomPaymentBlockParams): Promise<CreateCustomPaymentBlockResponse | undefined> => {
  try {
    const url = 'MyOrders/CreateCustomPaymentBlock';

    return (await axios.post<CreateCustomPaymentBlockResponse>(url, { ...params, language: 'en' })).data;
  } catch (err) {
    return undefined;
  }
};
export const createCustomPaymentLink = async (params: any): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/CreateCustomPaymentLink';
    return (await axios.post<MyOrderResponse>(url, { ...params, language: 'en' })).data;
  } catch (err) {
    return undefined;
  }
};
export const checkCustomPaymentStatus = async (transactionId: string): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/CheckCustomPaymentStatus';
    return (await axios.post<MyOrderResponse>(url, { transactionId })).data;
  } catch (err) {
    return undefined;
  }
};


interface UpdatePaymentBlockParams {
  sourcePrice: number;
  packageCurrency: string;
  addPrice: number;
  searchToken?: string;
  lang?: string;
}

export const updatePaymentBlock = async (params: UpdatePaymentBlockParams) => {
  try {
    const url = 'Utils/UpdatePaymentBlock';
    return (await axios.post(url, undefined, { params })).data;
  } catch (err) {
    return undefined;
  }
};

export const getMyOrdersCurrencies = async (): Promise<Select[]> => {
  try {
    const url = 'MyOrders/MyOrdersCurrencies';
    const res = (await axios.post(url)).data.currencies;
    let currencies: Select[] = [];

    for (const [key, value] of Object.entries(res)) {
      currencies.push({ value: key as string, label: value as string });
    }
    return currencies;
  } catch (err) {
    return [];
  }
};

export const getRandomName = async (
  maleCount: number = 1,
  femaleCount: number = 0
): Promise<any | undefined> => {
  try {
    const url = 'Utils/FakeNames';
    return (await axios.post(url, { maleCount, femaleCount })).data;
  } catch (err) {
    return undefined;
  }
};

interface ChangeSegmentEditorParams {
  order: Order;
  changePrice: boolean;
}
export const changeSegmentEditor = async (params: ChangeSegmentEditorParams): Promise<MyOrder | undefined> => {
  try {
    const url = 'MyOrders/ChangeSegmentEditor';
    return (await axios.post(url, params)).data?.order;
  } catch (err) {
    return undefined;
  }
};

interface GetCommisionParams {
  supplierCode: string;
  productType: string;
}
export const getCommision = async (params: GetCommisionParams): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/GetCommision';
    return (await axios.post(url, params)).data?.commision;
  } catch (err) {
    return undefined;
  }
};


export const segmentEditorOther = async (): Promise<MoSegmentEditorOtherResponse | undefined> => {
  try {
    const url = 'MyOrders/SegmentEditorOther';
    return (await axios.post(url)).data;
  } catch (err) {
    return undefined;
  }
};
interface ManualBookingParams {
  order?: any;
  hotel?: any;
  departmentId?: number;
  branchId?: number;
  userCode?: number;
  pasteJSON?: string;
}
export const manualBooking = async (params: ManualBookingParams): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/ManualBooking';
    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
}

export interface SaveReportParams {
  orderId: number;
  segmentId: number;
  originalAmount: number;
  refundAmount: number;
  ncRefundAmount: number;
  currency: string;
  remark: string;
}
export const saveReport = async (
  params: SaveReportParams
): Promise<MyOrderResponse | undefined> => {
  try {
    const url = 'MyOrders/SaveReport';
    return (await axios.post<MyOrderResponse>(url, params)).data;
  } catch (err) {
    return undefined;
  }
};
interface CloseRequestParams {
  orderId: number;
  segmentId: number;
  remarkId: string;
}
export const closeRequest = async (params: CloseRequestParams): Promise<any | undefined> => {
  try {
    const url = 'MyOrders/CloseMessageRequest';
    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};